import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ClearIcon from '@mui/icons-material/Clear';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useNavigate } from 'react-router-dom';
import { reviewApi } from '../services/reviewApi';

// ── Constants ───────────────────────────────────────────────────────────────
const SIDEBAR_WIDTH = 330;
const TEXT_PANEL_WIDTH = 420;
const STATUS_COLORS = {
  SONO: 'success',
  IFA: 'warning',
  IFR: 'info',
  REJ: 'error',
};
const PAGE_SIZE = 100;

const RESPLIT_INIT = {
  open: false,
  step: 'input',   // 'input' | 'preview'
  pageStart: 1,
  pageEnd: 1,
  loading: false,
  previewData: null,   // { metadata, page_infos, missing_cache_pages, source_pdf_page_count }
  previewBlobUrl: null,
  draft: {},           // campos editables pre-rellenados desde previewData.metadata
  error: '',
  saving: false,
};

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusChip({ status }) {
  return (
    <Chip
      label={status || '—'}
      color={STATUS_COLORS[status] || 'default'}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.65rem' }}
    />
  );
}
StatusChip.propTypes = { status: PropTypes.string };

function MetaRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
}
MetaRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ── Hook principal ───────────────────────────────────────────────────────────

function useReview() {
  const [groups, setGroups] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [filters, setFilters] = useState({ statuses: [], disciplines: [] });
  // Set de itr_numbers con el acordeón abierto
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // PK del ITRRecord actualmente seleccionado
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageText, setPageText] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [dhFilter, setDhFilter] = useState(false);
  const [nonStdRevFilter, setNonStdRevFilter] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingText, setLoadingText] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [listError, setListError] = useState('');
  const [saveError, setSaveError] = useState('');

  // Estado del modal de cambio de rango de páginas
  const [resplitModal, setResplitModal] = useState(RESPLIT_INIT);
  const searchTimeout = useRef(null);
  // 'first' | 'last' — qué versión seleccionar tras cambio de página por navegación
  const selectAfterLoad = useRef(null);

  // Lista plana de todos los IDs de versión en orden del sidebar (para prev/next)
  const allVersionIds = useMemo(
    () => groups.flatMap((g) => g.versions.map((v) => v.id)),
    [groups]
  );

  useEffect(() => {
    reviewApi.getFilters().then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  // Cargar lista agrupada al cambiar filtros o página
  useEffect(() => {
    setLoadingList(true);
    setListError('');
    reviewApi
      .listItrGroups({
        search: search || undefined,
        status: statusFilter || undefined,
        discipline: disciplineFilter || undefined,
        dh: dhFilter || undefined,
        non_std_rev: nonStdRevFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((r) => {
        const newGroups = r.data.results;
        setGroups(newGroups);
        setTotalCount(r.data.count);
        setHasNext(r.data.next);
        setHasPrev(r.data.previous);
        const allIds = newGroups.flatMap((g) => g.versions.map((v) => v.id));
        if (selectAfterLoad.current === 'last') {
          setSelectedId(allIds[allIds.length - 1] ?? null);
        } else if (typeof selectAfterLoad.current === 'number') {
          // Saltar al grupo en la posición relativa dentro de la página
          const targetGroup = newGroups[selectAfterLoad.current];
          const targetId = targetGroup
            ? targetGroup.versions[targetGroup.versions.length - 1]?.id ?? allIds[0] ?? null
            : allIds[0] ?? null;
          setSelectedId(targetId);
        } else {
          setSelectedId(allIds[0] ?? null);
        }
        selectAfterLoad.current = null;
      })
      .catch(() => setListError('Error loading ITR list'))
      .finally(() => setLoadingList(false));
  }, [search, statusFilter, disciplineFilter, dhFilter, nonStdRevFilter, page]);

  // Auto-expandir el grupo que contiene la versión seleccionada (si tiene >1 versión)
  useEffect(() => {
    if (!selectedId || !groups.length) return;
    const group = groups.find((g) => g.versions.some((v) => v.id === selectedId));
    if (group && group.version_count > 1) {
      setExpandedGroups((prev) => {
        if (prev.has(group.itr_number)) return prev;
        const next = new Set(prev);
        next.add(group.itr_number);
        return next;
      });
    }
  }, [selectedId, groups]);

  // Cargar detalle al cambiar selectedId
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setPageText('');
      setSelectedPage(null);
      return;
    }
    setLoadingDetail(true);
    setDetail(null);
    setPageText('');
    setSelectedPage(null);
    reviewApi
      .getItr(selectedId)
      .then((res) => {
        setDetail(res.data);
        if (res.data.pages?.length > 0) {
          setSelectedPage(res.data.pages[0].page_num);
        }
      })
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  // Cargar texto de página
  useEffect(() => {
    if (!detail || selectedPage === null) return;
    setLoadingText(true);
    setPageText('');
    reviewApi
      .getPageText(detail.id, selectedPage)
      .then((res) => setPageText(res.data.text))
      .catch(() => setPageText('— Text not available for this page —'))
      .finally(() => setLoadingText(false));
  }, [detail, selectedPage]);

  const goToGroup = useCallback((globalIdx) => {
    // globalIdx es 1-based (posición global del grupo)
    const targetPage = Math.ceil(globalIdx / PAGE_SIZE);
    const posInPage = (globalIdx - 1) % PAGE_SIZE; // 0-based dentro de la página
    selectAfterLoad.current = posInPage;
    setPage(targetPage);
  }, []);

  const goNext = useCallback(() => {
    const idx = allVersionIds.indexOf(selectedId);
    if (idx >= 0 && idx < allVersionIds.length - 1) {
      setSelectedId(allVersionIds[idx + 1]);
    } else if (hasNext) {
      selectAfterLoad.current = 'first';
      setPage((p) => p + 1);
    }
  }, [selectedId, allVersionIds, hasNext]);

  const goPrev = useCallback(() => {
    const idx = allVersionIds.indexOf(selectedId);
    if (idx > 0) {
      setSelectedId(allVersionIds[idx - 1]);
    } else if (hasPrev) {
      selectAfterLoad.current = 'last';
      setPage((p) => p - 1);
    }
  }, [selectedId, allVersionIds, hasPrev]);

  const toggleExpand = useCallback((itrNumber) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(itrNumber)) next.delete(itrNumber);
      else next.add(itrNumber);
      return next;
    });
  }, []);

  const handleSearchChange = useCallback((val) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  }, []);

  const resetPage = useCallback(() => setPage(1), []);

  const saveDetail = useCallback((itrId, patch) => {
    setSavingDetail(true);
    setSaveError('');
    const SUMMARY_KEYS = new Set(['status', 'revision', 'company', 'rta_discipline_code', 'rta_discipline', 'transmittal']);
    return reviewApi
      .updateItr(itrId, patch)
      .then(() => {
        setDetail((prev) => prev ? { ...prev, ...patch } : prev);
        // Actualizar también el resumen de versión visible en el sidebar
        setGroups((prev) =>
          prev.map((g) => {
            const hasVersion = g.versions.some((v) => v.id === itrId);
            const updatedVersions = g.versions.map((v) =>
              v.id === itrId
                ? { ...v, ...Object.fromEntries(Object.entries(patch).filter(([k]) => SUMMARY_KEYS.has(k))) }
                : v
            );
            // Si se renombra el itr_number, actualizar también la cabecera del grupo
            const updatedItrNumber = hasVersion && patch.itr_number ? patch.itr_number : g.itr_number;
            return { ...g, itr_number: updatedItrNumber, versions: updatedVersions };
          })
        );
      })
      .catch(() => setSaveError('Error saving changes'))
      .finally(() => setSavingDetail(false));
  }, []);

  // ── Resplit handlers ────────────────────────────────────────────────────────

  const openResplitModal = useCallback(() => {
    if (!detail) return;
    // Parsear page_range actual para pre-rellenar los campos
    const rangeMatch = (detail.page_range || '').match(/^(\d+)-(\d+)$/);
    setResplitModal((prev) => ({
      ...RESPLIT_INIT,
      open: true,
      pageStart: rangeMatch ? parseInt(rangeMatch[1], 10) : 1,
      pageEnd:   rangeMatch ? parseInt(rangeMatch[2], 10) : 1,
    }));
  }, [detail]);

  const closeResplitModal = useCallback(() => {
    setResplitModal((prev) => {
      if (prev.previewBlobUrl) URL.revokeObjectURL(prev.previewBlobUrl);
      return RESPLIT_INIT;
    });
  }, []);

  const handleResplitPreview = useCallback((pageStart, pageEnd) => {
    if (!detail) return;
    setResplitModal((prev) => ({ ...prev, loading: true, error: '' }));
    reviewApi
      .resplitPreview(detail.id, pageStart, pageEnd)
      .then((res) => {
        // Cargar el PDF de previsualización generado en el backend
        return reviewApi.getResplitPreviewPdfBlob(detail.id).then((blobRes) => {
          const blobUrl = URL.createObjectURL(blobRes.data);
          const META_KEYS = [
            'itr_number', 'revision', 'doc_date', 'status', 'description',
            'company', 'location', 'transmittal', 'cluster_code',
            'max_asset_location', 'rta_discipline', 'rta_discipline_code',
            'rta_system', 'rta_system_code', 'rta_sub_system', 'rta_sub_system_code',
            'rta_element', 'rta_element_code',
          ];
          const draft = {};
          META_KEYS.forEach((k) => { draft[k] = res.data.metadata[k] ?? ''; });
          setResplitModal((prev) => {
            if (prev.previewBlobUrl) URL.revokeObjectURL(prev.previewBlobUrl);
            return {
              ...prev,
              step: 'preview',
              loading: false,
              previewData: res.data,
              previewBlobUrl: blobUrl,
              draft,
            };
          });
        });
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'Error generando la previsualización';
        setResplitModal((prev) => ({ ...prev, loading: false, error: msg }));
      });
  }, [detail]);

  const handleConfirmResplit = useCallback((pageStart, pageEnd, metadata) => {
    if (!detail) return;
    setResplitModal((prev) => ({ ...prev, saving: true, error: '' }));
    reviewApi
      .confirmResplit(detail.id, pageStart, pageEnd, metadata)
      .then((res) => {
        setDetail(res.data);
        setResplitModal((prev) => {
          if (prev.previewBlobUrl) URL.revokeObjectURL(prev.previewBlobUrl);
          return RESPLIT_INIT;
        });
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'Error al confirmar el cambio de rango';
        setResplitModal((prev) => ({ ...prev, saving: false, error: msg }));
      });
  }, [detail]);

  return {
    groups,
    totalCount,
    page,
    hasNext,
    hasPrev,
    filters,
    expandedGroups,
    toggleExpand,
    selectedId,
    setSelectedId,
    detail,
    selectedPage,
    setSelectedPage,
    pageText,
    handleSearchChange,
    statusFilter,
    setStatusFilter: (v) => { setStatusFilter(v); resetPage(); },
    disciplineFilter,
    setDisciplineFilter: (v) => { setDisciplineFilter(v); resetPage(); },
    dhFilter,
    setDhFilter: (v) => { setDhFilter(v); resetPage(); },
    nonStdRevFilter,
    setNonStdRevFilter: (v) => { setNonStdRevFilter(v); resetPage(); },
    loadingList,
    loadingDetail,
    loadingText,
    savingDetail,
    listError,
    saveError,
    setSaveError,
    goNext,
    goPrev,
    goToGroup,
    saveDetail,
    setPage,
    resplitModal,
    openResplitModal,
    closeResplitModal,
    handleResplitPreview,
    handleConfirmResplit,
    setResplitModal,
  };
}

// ── ItrSidebar ───────────────────────────────────────────────────────────────

function ItrSidebar({
  groups, totalCount, page,
  hasNext, hasPrev,
  selectedId, setSelectedId,
  goPrev, goNext, goToGroup,
  expandedGroups, toggleExpand,
  handleSearchChange, statusFilter, setStatusFilter,
  disciplineFilter, setDisciplineFilter, filters,
  dhFilter, setDhFilter,
  nonStdRevFilter, setNonStdRevFilter,
  loadingList, listError,
}) {
  const listRef = useRef(null);
  const [inputVal, setInputVal] = useState('');
  const [navEditing, setNavEditing] = useState(false);
  const [navInput, setNavInput] = useState('');

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const allVersionIds = groups.flatMap((g) => g.versions.map((v) => v.id));
  const selectedIdx = allVersionIds.indexOf(selectedId);
  const isAtStart = selectedIdx <= 0 && !hasPrev;
  const isAtEnd = (selectedIdx >= allVersionIds.length - 1) && !hasNext;
  // Posición global dentro del total de ITR numbers (grupos, no versiones)
  const selectedGroupIdx = groups.findIndex((g) => g.versions.some((v) => v.id === selectedId));
  const globalGroupIdx = selectedGroupIdx >= 0 ? (page - 1) * PAGE_SIZE + selectedGroupIdx + 1 : '—';

  const handleNavJump = () => {
    const n = parseInt(navInput, 10);
    if (!isNaN(n) && n >= 1 && n <= totalCount) {
      goToGroup(n);
    }
    setNavEditing(false);
  };

  return (
    <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', height: '100%', overflow: 'hidden' }}>
      {/* Filtros */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search ITR, transmittal, company…"
          value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); handleSearchChange(e.target.value); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: inputVal ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setInputVal(''); handleSearchChange(''); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          fullWidth
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {filters.statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Discipline</InputLabel>
            <Select value={disciplineFilter} label="Discipline" onChange={(e) => setDisciplineFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {filters.disciplines.map((d) => <MenuItem key={d.code} value={d.code}>{d.code} — {d.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dhFilter}
              onChange={(e) => setDhFilter(e.target.checked)}
              sx={{ py: 0 }}
            />
          }
          label={<Typography variant="caption">DH only</Typography>}
          sx={{ ml: 0 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={nonStdRevFilter}
              onChange={(e) => setNonStdRevFilter(e.target.checked)}
              sx={{ py: 0 }}
            />
          }
          label={<Typography variant="caption">Revisión no estándar</Typography>}
          sx={{ ml: 0 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
          {totalCount.toLocaleString()} ITRs · pg. {page}/{totalPages}
        </Typography>
      </Box>
      <Divider />

      {/* Lista agrupada */}
      <Box sx={{ flex: 1, overflow: 'auto' }} ref={listRef}>
        {loadingList && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>}
        {listError && <Alert severity="error" sx={{ m: 1 }}>{listError}</Alert>}
        {!loadingList && !listError && groups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No results.</Typography>
        )}
        <List dense disablePadding>
          {groups.map((group) => {
            const isMulti = group.version_count > 1;
            const isGroupActive = group.versions.some((v) => v.id === selectedId);
            const isExpanded = isMulti && (expandedGroups.has(group.itr_number) || isGroupActive);
            // La versión representativa: la seleccionada si pertenece al grupo, si no la última
            const representative = isGroupActive
              ? group.versions.find((v) => v.id === selectedId)
              : group.versions[group.versions.length - 1];

            return (
              <React.Fragment key={group.itr_number}>
                {/* Cabecera del grupo */}
                <ListItemButton
                  data-selected={!isMulti && representative?.id === selectedId ? 'true' : undefined}
                  selected={isGroupActive}
                  onClick={() => {
                    if (isMulti) toggleExpand(group.itr_number);
                    // Si no hay versión activa en este grupo, seleccionar la última
                    if (!isGroupActive) setSelectedId(group.versions[group.versions.length - 1].id);
                  }}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-all', flex: 1 }}>
                          {group.itr_number}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                          {isMulti && (
                            <Chip
                              label={`${group.version_count} rev.`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                          <StatusChip status={representative?.status} />
                          {representative?.has_pdf && (
                            <Tooltip title="PDF available"><PictureAsPdfIcon color="error" sx={{ fontSize: 14 }} /></Tooltip>
                          )}
                          {isMulti && (
                            isExpanded
                              ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {representative?.description}
                      </Typography>
                    }
                  />
                </ListItemButton>

                {/* Sub-ítems de versiones (sólo grupos con varias revisiones) */}
                {isMulti && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {group.versions.map((v) => (
                      <ListItemButton
                        key={v.id}
                        data-selected={v.id === selectedId ? 'true' : undefined}
                        selected={v.id === selectedId}
                        onClick={() => setSelectedId(v.id)}
                        sx={{ pl: 3.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {v.revision ? `Rev. ${v.revision}` : (v.transmittal || '—')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <StatusChip status={v.status} />
                                {v.has_pdf && <PictureAsPdfIcon color="error" sx={{ fontSize: 12 }} />}
                              </Box>
                            </Box>
                          }
                          secondary={v.revision && v.transmittal ? (
                            <Typography variant="caption" color="text.secondary">
                              {v.transmittal}
                            </Typography>
                          ) : undefined}
                        />
                      </ListItemButton>
                    ))}
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Navegación */}
      <Divider />
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper' }}>
        <IconButton size="small" onClick={goPrev} disabled={isAtStart}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        {navEditing ? (
          <TextField
            size="small"
            value={navInput}
            onChange={(e) => setNavInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNavJump();
              if (e.key === 'Escape') setNavEditing(false);
            }}
            onBlur={handleNavJump}
            autoFocus
            inputProps={{ style: { textAlign: 'center', width: 56, fontSize: '0.72rem', padding: '2px 4px' } }}
            sx={{ mx: 0.5 }}
          />
        ) : (
          <Tooltip title={`Ir a posición (1–${totalCount})`}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: groups.length > 0 ? 'pointer' : 'default', '&:hover': groups.length > 0 ? { textDecoration: 'underline' } : {} }}
              onClick={() => {
                if (groups.length === 0) return;
                setNavInput(String(globalGroupIdx));
                setNavEditing(true);
              }}
            >
              {groups.length > 0 ? `${globalGroupIdx} / ${totalCount}` : '0 / 0'}
            </Typography>
          </Tooltip>
        )}
        <IconButton size="small" onClick={goNext} disabled={isAtEnd}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
ItrSidebar.propTypes = {
  groups: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  hasNext: PropTypes.bool.isRequired,
  hasPrev: PropTypes.bool.isRequired,
  selectedId: PropTypes.number,
  setSelectedId: PropTypes.func.isRequired,
  goPrev: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  goToGroup: PropTypes.func.isRequired,
  expandedGroups: PropTypes.instanceOf(Set).isRequired,
  toggleExpand: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  disciplineFilter: PropTypes.string.isRequired,
  setDisciplineFilter: PropTypes.func.isRequired,
  filters: PropTypes.shape({ statuses: PropTypes.array, disciplines: PropTypes.array }).isRequired,
  dhFilter: PropTypes.bool.isRequired,
  setDhFilter: PropTypes.func.isRequired,
  nonStdRevFilter: PropTypes.bool.isRequired,
  setNonStdRevFilter: PropTypes.func.isRequired,
  loadingList: PropTypes.bool.isRequired,
  listError: PropTypes.string.isRequired,
};

// ── PdfPanel ─────────────────────────────────────────────────────────────────

function usePdfBlob(itrId, hasPdf, fetcher) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');

  useEffect(() => {
    setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPdfError('');
    if (!itrId || !hasPdf) return;

    setPdfLoading(true);
    fetcher(itrId)
      .then((res) => setBlobUrl(URL.createObjectURL(res.data)))
      .catch(() => setPdfError('Error loading PDF'))
      .finally(() => setPdfLoading(false));

    return () => setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
  }, [itrId, hasPdf]); // fetcher is stable (module-level function reference)

  return { blobUrl, pdfLoading, pdfError };
}

function PdfViewer({ itrId, hasPdf, loading, blobUrl, pdfLoading, pdfError, emptyLabel }) {
  if (loading || pdfLoading)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
  if (!itrId)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="text.secondary">Select an ITR</Typography></Box>;
  if (!hasPdf)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="text.secondary">{emptyLabel}</Typography></Box>;
  if (pdfError)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Alert severity="error">{pdfError}</Alert></Box>;
  if (!blobUrl) return null;
  return (
    <Box component="iframe" key={blobUrl} src={`${blobUrl}#zoom=FitH`} title={`PDF — ${itrId}`}
      sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
  );
}
PdfViewer.propTypes = {
  itrId: PropTypes.number,
  hasPdf: PropTypes.bool,
  loading: PropTypes.bool,
  blobUrl: PropTypes.string,
  pdfLoading: PropTypes.bool,
  pdfError: PropTypes.string,
  emptyLabel: PropTypes.string.isRequired,
};

function PdfPanel({ itrId, hasPdf, hasSourcePdf, sourcePageRange, loading }) {
  const [tab, setTab] = useState(0);

  // Reiniciar a la pestaña del split PDF cuando cambia el ITR
  useEffect(() => { setTab(0); }, [itrId]);

  const split = usePdfBlob(itrId, hasPdf, reviewApi.getPdfBlob);
  const origin = usePdfBlob(itrId, hasSourcePdf, reviewApi.getOriginPdfBlob);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36, bgcolor: 'background.paper',
              '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem' } }}
      >
        <Tab
          icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Extracted PDF (split)"
          disabled={!hasPdf}
        />
        <Tab
          icon={<PictureAsPdfIcon sx={{ fontSize: 16, color: hasSourcePdf ? 'warning.main' : 'inherit' }} />}
          iconPosition="start"
          label={`Source PDF${sourcePageRange ? ` · pp. ${sourcePageRange}` : ''}`}
          disabled={!hasSourcePdf}
        />
      </Tabs>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {tab === 0 && (
          <PdfViewer
            itrId={itrId} hasPdf={hasPdf} loading={loading}
            blobUrl={split.blobUrl} pdfLoading={split.pdfLoading} pdfError={split.pdfError}
            emptyLabel="PDF not available"
          />
        )}
        {tab === 1 && (
          <PdfViewer
            itrId={itrId} hasPdf={hasSourcePdf} loading={loading}
            blobUrl={origin.blobUrl} pdfLoading={origin.pdfLoading} pdfError={origin.pdfError}
            emptyLabel="Source PDF not available"
          />
        )}
      </Box>
    </Box>
  );
}
PdfPanel.propTypes = {
  itrId: PropTypes.number,
  hasPdf: PropTypes.bool,
  hasSourcePdf: PropTypes.bool,
  sourcePageRange: PropTypes.string,
  loading: PropTypes.bool,
};

// ── Hook RTA catalogue ────────────────────────────────────────────────────────

function useRtaCatalogue() {
  const [catalogue, setCatalogue] = useState(null);
  useEffect(() => {
    reviewApi.getRtaCatalogue()
      .then((r) => setCatalogue(r.data))
      .catch(() => setCatalogue({}));
  }, []);
  return catalogue;
}

// ── PageRangeModal ────────────────────────────────────────────────────────────

function ResplitMetaForm({ draft, setDraft, currentDetail, catalogue }) {
  const PLAIN_RESPLIT_FIELDS = [
    { key: 'itr_number',          label: 'ITR Number' },
    { key: 'revision',            label: 'Revision' },
    { key: 'doc_date',            label: 'Doc. Date' },
    { key: 'status',              label: 'Status' },
    { key: 'description',         label: 'Description' },
    { key: 'company',             label: 'Company' },
    { key: 'location',            label: 'Location' },
    { key: 'transmittal',  label: 'Transmittal' },
    { key: 'cluster_code',        label: 'Cluster' },
    { key: 'max_asset_location',  label: 'Max. asset location' },
  ];

  const discOptions = catalogue
    ? Object.entries(catalogue).map(([code, d]) => ({ code, name: d.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];
  const selectedDisc = catalogue?.[draft.rta_discipline_code];
  const sysOptions = selectedDisc
    ? Object.entries(selectedDisc.systems).map(([code, s]) => ({ code, name: s.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];
  const selectedSys = selectedDisc?.systems?.[draft.rta_system_code];
  const ssOptions = selectedSys
    ? Object.entries(selectedSys.sub_systems).map(([code, ss]) => ({ code, name: ss.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];
  const selectedSs = selectedSys?.sub_systems?.[draft.rta_sub_system_code];
  const elOptions = selectedSs
    ? Object.entries(selectedSs.elements).map(([code, name]) => ({ code, name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const handleDiscChange = (code) => {
    const name = catalogue?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_discipline_code: code, rta_discipline: name, rta_system_code: '', rta_system: '', rta_sub_system_code: '', rta_sub_system: '', rta_element_code: '', rta_element: '' }));
  };
  const handleSysChange = (code) => {
    const name = selectedDisc?.systems?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_system_code: code, rta_system: name, rta_sub_system_code: '', rta_sub_system: '', rta_element_code: '', rta_element: '' }));
  };
  const handleSsChange = (code) => {
    const name = selectedSys?.sub_systems?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_sub_system_code: code, rta_sub_system: name, rta_element_code: '', rta_element: '' }));
  };
  const handleElChange = (code) => {
    const name = selectedSs?.elements?.[code] || '';
    setDraft((d) => ({ ...d, rta_element_code: code, rta_element: name }));
  };

  // Indica si un campo cambió respecto al valor original del registro
  const isDiff = (key) => (draft[key] || '') !== (currentDetail?.[key] || '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
      {PLAIN_RESPLIT_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          label={label}
          value={draft[key] ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            style: { fontSize: '0.75rem', backgroundColor: isDiff(key) ? '#fff8e1' : undefined },
          }}
          InputLabelProps={{ style: { fontSize: '0.75rem' } }}
        />
      ))}
      <Divider sx={{ my: 0.5 }}><Typography variant="caption" color="text.secondary">RTA Catalogue</Typography></Divider>
      <RtaSelect label="Discipline" value={draft.rta_discipline_code ?? ''} onChange={handleDiscChange} options={discOptions} disabled={!catalogue} />
      <RtaSelect label="System" value={draft.rta_system_code ?? ''} onChange={handleSysChange} options={sysOptions} disabled={!draft.rta_discipline_code} />
      <RtaSelect label="Sub-system" value={draft.rta_sub_system_code ?? ''} onChange={handleSsChange} options={ssOptions} disabled={!draft.rta_system_code} />
      <RtaSelect label="Element" value={draft.rta_element_code ?? ''} onChange={handleElChange} options={elOptions} disabled={!draft.rta_sub_system_code} />
    </Box>
  );
}
ResplitMetaForm.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  currentDetail: PropTypes.object,
  catalogue: PropTypes.object,
};

function PageRangeModal({ open, step, pageStart, pageEnd, loading, previewData, previewBlobUrl,
  draft, setDraft, error, saving, currentDetail, catalogue,
  onClose, onPreview, onBack, onConfirm }) {

  const [localStart, setLocalStart] = useState(String(pageStart));
  const [localEnd,   setLocalEnd]   = useState(String(pageEnd));
  const [inputError, setInputError] = useState('');

  // Sincronizar campos numéricos cuando el modal se abre
  useEffect(() => {
    if (open && step === 'input') {
      setLocalStart(String(pageStart));
      setLocalEnd(String(pageEnd));
      setInputError('');
    }
  }, [open, step, pageStart, pageEnd]);

  const validateAndPreview = () => {
    const start = parseInt(localStart, 10);
    const end   = parseInt(localEnd,   10);
    const maxPage = previewData?.source_pdf_page_count || 99999;
    if (!start || start < 1) { setInputError('Start page must be ≥ 1'); return; }
    if (!end   || end < start) { setInputError('End page must be ≥ start page'); return; }
    if (end > maxPage) { setInputError(`End page exceeds source PDF pages (${maxPage})`); return; }
    setInputError('');
    onPreview(start, end);
  };

  const handleConfirm = () => {
    const start = parseInt(localStart, 10);
    const end   = parseInt(localEnd,   10);
    onConfirm(start, end, draft);
  };

  const sourceTotalPages = previewData?.source_pdf_page_count || 0;

  return (
    <Dialog
      open={open}
      onClose={saving || loading ? undefined : onClose}
      maxWidth={step === 'preview' ? 'xl' : 'sm'}
      fullWidth
      PaperProps={{ sx: { height: step === 'preview' ? '90vh' : 'auto' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCutIcon sx={{ fontSize: 20 }} />
          <Typography variant="h6" component="span">Change Page Range</Typography>
          {currentDetail && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {currentDetail.itr_number}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      {/* ── Step 1: input ── */}
      {step === 'input' && (
        <>
          <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {currentDetail?.page_range && (
                  <Typography variant="body2" color="text.secondary">
                    Current range: <strong>{currentDetail.page_range}</strong>
                  </Typography>
                )}
                {sourceTotalPages > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    · Source PDF: <strong>{sourceTotalPages} pages</strong>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start page"
                  type="number"
                  value={localStart}
                  onChange={(e) => setLocalStart(e.target.value)}
                  size="small"
                  inputProps={{ min: 1, max: sourceTotalPages || undefined }}
                  sx={{ width: 140 }}
                />
                <TextField
                  label="End page"
                  type="number"
                  value={localEnd}
                  onChange={(e) => setLocalEnd(e.target.value)}
                  size="small"
                  inputProps={{ min: 1, max: sourceTotalPages || undefined }}
                  sx={{ width: 140 }}
                />
              </Box>
              {inputError && <Alert severity="warning" sx={{ py: 0, fontSize: '0.78rem' }}>{inputError}</Alert>}
              {error && <Alert severity="error" sx={{ py: 0, fontSize: '0.78rem' }}>{error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={validateAndPreview}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {loading ? 'Generating preview…' : 'Preview'}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 2: preview ── */}
      {step === 'preview' && (
        <>
          <DialogContent sx={{ pt: 1, overflow: 'hidden', display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            {/* PDF preview (izquierda) */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Preview — pages {localStart}–{localEnd}
                {previewData?.missing_cache_pages?.length > 0 && (
                  <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                    · Cache missing for pages: {previewData.missing_cache_pages.join(', ')}
                  </Typography>
                )}
              </Typography>
              {previewBlobUrl ? (
                <Box
                  component="iframe"
                  src={`${previewBlobUrl}#zoom=FitH`}
                  title="Preview PDF"
                  sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 0 }}
                />
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>

            {/* Metadata form (derecha) */}
            <Box sx={{ width: 320, flexShrink: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Verify and edit extracted metadata before saving.
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, gap: 0.3 }}>
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#fff8e1', border: '1px solid #f0e0a0', borderRadius: '2px' }} />
                  = changed from current value
                </Box>
              </Typography>
              <ResplitMetaForm
                draft={draft}
                setDraft={setDraft}
                currentDetail={currentDetail}
                catalogue={catalogue}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            {error && <Alert severity="error" sx={{ py: 0, fontSize: '0.78rem', flex: 1, mr: 1 }}>{error}</Alert>}
            <Button onClick={onBack} disabled={saving}>Back</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirm}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
PageRangeModal.propTypes = {
  open:           PropTypes.bool.isRequired,
  step:           PropTypes.oneOf(['input', 'preview']).isRequired,
  pageStart:      PropTypes.number.isRequired,
  pageEnd:        PropTypes.number.isRequired,
  loading:        PropTypes.bool.isRequired,
  previewData:    PropTypes.object,
  previewBlobUrl: PropTypes.string,
  draft:          PropTypes.object.isRequired,
  setDraft:       PropTypes.func.isRequired,
  error:          PropTypes.string.isRequired,
  saving:         PropTypes.bool.isRequired,
  currentDetail:  PropTypes.object,
  catalogue:      PropTypes.object,
  onClose:        PropTypes.func.isRequired,
  onPreview:      PropTypes.func.isRequired,
  onBack:         PropTypes.func.isRequired,
  onConfirm:      PropTypes.func.isRequired,
};

// ── TextPanel ─────────────────────────────────────────────────────────────────

// Campos editables expuestos en el panel de metadatos (no-RTA)
const STATUS_OPTIONS = ['', 'SONO', 'NOWC', 'REJECTED'];

const PLAIN_META_FIELDS = [
  { key: 'revision',           label: 'Revision' },
  { key: 'company',            label: 'Company' },
  { key: 'location',           label: 'Location' },
  { key: 'transmittal', label: 'Transmittal' },
  { key: 'cluster_code',       label: 'Cluster' },
  { key: 'max_asset_location', label: 'Max. asset location' },
];

// Selector genérico reutilizable para los niveles RTA
function RtaSelect({ label, value, onChange, options, disabled }) {
  return (
    <FormControl size="small" fullWidth disabled={disabled || options.length === 0}>
      <InputLabel style={{ fontSize: '0.75rem' }}>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onChange(e.target.value)} style={{ fontSize: '0.75rem' }}>
        <MenuItem value=""><em>— None —</em></MenuItem>
        {options.map(({ code, name }) => (
          <MenuItem key={code} value={code} style={{ fontSize: '0.75rem' }}>
            {code}{name ? ` — ${name}` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
RtaSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, name: PropTypes.string })).isRequired,
  disabled: PropTypes.bool,
};

function TextPanel({ detail, selectedPage, setSelectedPage, pageText, loadingDetail, loadingText, onSave, savingDetail, saveError, catalogue, onOpenResplit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  // Resetear draft cuando cambia el ITR o se sale del modo edición
  useEffect(() => {
    setEditing(false);
    setDraft({});
  }, [detail?.id]);

  if (loadingDetail) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress size={28} /></Box>;
  if (!detail) return <Box sx={{ p: 2 }}><Typography color="text.secondary" variant="body2">No data</Typography></Box>;

  const pageIdx = detail.pages?.findIndex((p) => p.page_num === selectedPage) ?? 0;

  // Opciones derivadas del catálogo según selección en cascada
  const discOptions = catalogue
    ? Object.entries(catalogue).map(([code, d]) => ({ code, name: d.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const selectedDisc = catalogue?.[draft.rta_discipline_code];
  const sysOptions = selectedDisc
    ? Object.entries(selectedDisc.systems).map(([code, s]) => ({ code, name: s.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const selectedSys = selectedDisc?.systems?.[draft.rta_system_code];
  const ssOptions = selectedSys
    ? Object.entries(selectedSys.sub_systems).map(([code, ss]) => ({ code, name: ss.name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const selectedSs = selectedSys?.sub_systems?.[draft.rta_sub_system_code];
  const elOptions = selectedSs
    ? Object.entries(selectedSs.elements).map(([code, name]) => ({ code, name })).sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const handleEdit = () => {
    setDraft({
      itr_number:          detail.itr_number || '',
      status:              detail.status || '',
      revision:            detail.revision || '',
      company:             detail.company || '',
      location:            detail.location || '',
      transmittal:  detail.transmittal || '',
      cluster_code:        detail.cluster_code || '',
      max_asset_location:  detail.max_asset_location || '',
      rta_discipline_code: detail.rta_discipline_code || '',
      rta_discipline:      detail.rta_discipline || '',
      rta_system_code:     detail.rta_system_code || '',
      rta_system:          detail.rta_system || '',
      rta_sub_system_code: detail.rta_sub_system_code || '',
      rta_sub_system:      detail.rta_sub_system || '',
      rta_element_code:    detail.rta_element_code || '',
      rta_element:         detail.rta_element || '',
    });
    setEditing(true);
  };

  const handleDiscChange = (code) => {
    const name = catalogue?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_discipline_code: code, rta_discipline: name, rta_system_code: '', rta_system: '', rta_sub_system_code: '', rta_sub_system: '', rta_element_code: '', rta_element: '' }));
  };

  const handleSysChange = (code) => {
    const name = selectedDisc?.systems?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_system_code: code, rta_system: name, rta_sub_system_code: '', rta_sub_system: '', rta_element_code: '', rta_element: '' }));
  };

  const handleSsChange = (code) => {
    const name = selectedSys?.sub_systems?.[code]?.name || '';
    setDraft((d) => ({ ...d, rta_sub_system_code: code, rta_sub_system: name, rta_element_code: '', rta_element: '' }));
  };

  const handleElChange = (code) => {
    const name = selectedSs?.elements?.[code] || '';
    setDraft((d) => ({ ...d, rta_element_code: code, rta_element: name }));
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft({});
  };

  const handleSave = () => {
    const ALL_KEYS = [
      'itr_number',
      'status', 'revision', 'company', 'location', 'transmittal',
      'cluster_code', 'max_asset_location',
      'rta_discipline_code', 'rta_discipline', 'rta_system_code', 'rta_system',
      'rta_sub_system_code', 'rta_sub_system', 'rta_element_code', 'rta_element',
    ];
    const changed = {};
    ALL_KEYS.forEach((key) => {
      if (draft[key] !== (detail[key] || '')) changed[key] = draft[key];
    });
    if (Object.keys(changed).length === 0) { setEditing(false); return; }
    onSave(detail.id, changed).then(() => setEditing(false));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Metadata */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-all', flex: 1 }}>{detail.itr_number}</Typography>
          <StatusChip status={detail.status} />
          {!editing && (
            <Tooltip title="Edit fields">
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {!editing && detail.has_source_pdf && (
            <Tooltip title="Change page range">
              <IconButton size="small" onClick={onOpenResplit}>
                <ContentCutIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Nombre ITR */}
            <TextField
              label="ITR Number"
              value={draft.itr_number ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, itr_number: e.target.value }))}
              size="small"
              variant="outlined"
              fullWidth
              InputProps={{ style: { fontSize: '0.75rem' } }}
              InputLabelProps={{ style: { fontSize: '0.75rem' } }}
            />
            {/* Estado */}
            <FormControl size="small" fullWidth>
              <InputLabel style={{ fontSize: '0.75rem' }}>Status</InputLabel>
              <Select value={draft.status ?? ''} label="Status" onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} style={{ fontSize: '0.75rem' }}>
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt || '— No status —'}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Campos de texto planos */}
            {PLAIN_META_FIELDS.map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                value={draft[key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                size="small"
                variant="outlined"
                fullWidth
                InputProps={{ style: { fontSize: '0.75rem' } }}
                InputLabelProps={{ style: { fontSize: '0.75rem' } }}
              />
            ))}

            {/* Jerarquía RTA en cascada */}
            <Divider sx={{ my: 0.5 }}><Typography variant="caption" color="text.secondary">RTA Catalogue</Typography></Divider>
            <RtaSelect label="Discipline" value={draft.rta_discipline_code ?? ''} onChange={handleDiscChange} options={discOptions} disabled={!catalogue} />
            <RtaSelect label="System" value={draft.rta_system_code ?? ''} onChange={handleSysChange} options={sysOptions} disabled={!draft.rta_discipline_code} />
            <RtaSelect label="Sub-system" value={draft.rta_sub_system_code ?? ''} onChange={handleSsChange} options={ssOptions} disabled={!draft.rta_system_code} />
            <RtaSelect label="Element" value={draft.rta_element_code ?? ''} onChange={handleElChange} options={elOptions} disabled={!draft.rta_sub_system_code} />

            {saveError && <Alert severity="error" sx={{ py: 0, fontSize: '0.7rem' }}>{saveError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
              <Button size="small" startIcon={<UndoIcon />} onClick={handleCancel} disabled={savingDetail}>
                Cancel
              </Button>
              <Button size="small" variant="contained" startIcon={savingDetail ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={savingDetail}>
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <MetaRow label="Revision" value={detail.revision} />
            <MetaRow label="Company" value={detail.company} />
            <MetaRow label="Location" value={detail.location} />
            <MetaRow label="Transmittal" value={detail.transmittal} />
            <MetaRow label="Discipline" value={detail.rta_discipline ? `${detail.rta_discipline_code} — ${detail.rta_discipline}` : detail.rta_discipline_code} />
            <MetaRow label="System" value={detail.rta_system} />
            <MetaRow label="Sub-system" value={detail.rta_sub_system} />
            <MetaRow label="Element" value={detail.rta_element} />
            <MetaRow label="Cluster" value={detail.cluster_code} />
            <MetaRow label="Max. asset location" value={detail.max_asset_location} />
            <MetaRow label="Pages" value={detail.page_range} />
            <MetaRow label="Source PDF (GCS)" value={detail.source_pdf_path} />
            <MetaRow label="Created at" value={detail.creatated_at ? new Date(detail.creatated_at).toLocaleString() : null} />
            <MetaRow label="Updated at" value={detail.updated_at ? new Date(detail.updated_at).toLocaleString() : null} />
            {detail.description && <Box sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">{detail.description}</Typography></Box>}
          </>
        )}
      </Box>

      {/* Tabs de páginas */}
      {detail.pages?.length > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Tabs
              value={pageIdx >= 0 ? pageIdx : 0}
              onChange={(_, newIdx) => setSelectedPage(detail.pages[newIdx].page_num)}
              variant="scrollable" scrollButtons="auto"
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.7rem' } }}
            >
              {detail.pages.map((p) => <Tab key={p.page_num} label={`Pg. ${p.page_num}`} />)}
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
            {loadingText
              ? <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} /></Box>
              : <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.5 }}>
                  {pageText || '— No content —'}
                </Typography>
            }
          </Box>
        </>
      ) : (
        <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No extracted text for this ITR.</Typography></Box>
      )}
    </Box>
  );
}
TextPanel.propTypes = {
  detail: PropTypes.object,
  selectedPage: PropTypes.number,
  setSelectedPage: PropTypes.func.isRequired,
  pageText: PropTypes.string.isRequired,
  loadingDetail: PropTypes.bool.isRequired,
  loadingText: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  savingDetail: PropTypes.bool.isRequired,
  saveError: PropTypes.string.isRequired,
  catalogue: PropTypes.object,
  onOpenResplit: PropTypes.func.isRequired,
};

// ── ReviewPage ────────────────────────────────────────────────────────────────

function ReviewPage() {
  const navigate = useNavigate();
  const [importSnack, setImportSnack] = useState({ open: false, msg: '', severity: 'info' });
  const [exporting, setExporting] = useState(false);
  const catalogue = useRtaCatalogue();

  const {
    groups, totalCount, page, hasNext, hasPrev,
    filters, expandedGroups, toggleExpand,
    selectedId, setSelectedId, detail, selectedPage, setSelectedPage,
    pageText, handleSearchChange, statusFilter, setStatusFilter,
    disciplineFilter, setDisciplineFilter, dhFilter, setDhFilter,
    nonStdRevFilter, setNonStdRevFilter,
    loadingList, loadingDetail,
    loadingText, savingDetail, listError, saveError,
    goNext, goPrev, goToGroup, saveDetail,
    resplitModal, openResplitModal, closeResplitModal,
    handleResplitPreview, handleConfirmResplit, setResplitModal,
  } = useReview();

  const handleImport = () => {
    reviewApi
      .triggerImport()
      .then(() => setImportSnack({ open: true, msg: 'Import started in background', severity: 'success' }))
      .catch(() => setImportSnack({ open: true, msg: 'Error launching import', severity: 'error' }));
  };

  const handleExport = () => {
    setExporting(true);
    reviewApi
      .exportFdct()
      .then((res) => {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'FDCT_ITR_LOG_export.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setImportSnack({ open: true, msg: 'Error generating Excel export', severity: 'error' }))
      .finally(() => setExporting(false));
  };

  const handleSaveDetail = useCallback((itrId, patch) => {
    return saveDetail(itrId, patch).then(() => {
      setImportSnack({ open: true, msg: 'Changes saved successfully', severity: 'success' });
    });
  }, [saveDetail]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppBar position="static">
        <Toolbar variant="dense">
          <IconButton color="inherit" edge="start" onClick={() => navigate('/jobs')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
            Output Batch Review
          </Typography>
          {detail && (
            <Typography variant="caption" sx={{ opacity: 0.8, mr: 2 }}>
              {detail.itr_number}{detail.revision ? ` · Rev. ${detail.revision}` : detail.transmittal ? ` · ${detail.transmittal}` : ''}
            </Typography>
          )}
          <Tooltip title="Progress Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bundle Downloads">
            <IconButton color="inherit" onClick={() => navigate('/bundle')}>
              <FolderOpenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Import traceability from filesystem to DB">
            <Button
              color="inherit"
              size="small"
              startIcon={<CloudDownloadIcon />}
              onClick={handleImport}
              sx={{ textTransform: 'none' }}
            >
              Import
            </Button>
          </Tooltip>
          <Tooltip title="Export enriched FDCT ITR Log to Excel">
            <Button
              color="inherit"
              size="small"
              startIcon={<TableChartIcon />}
              onClick={handleExport}
              disabled={exporting}
              sx={{ textTransform: 'none', ml: 1 }}
            >
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ItrSidebar
          groups={groups} totalCount={totalCount} page={page}
          hasNext={hasNext} hasPrev={hasPrev}
          selectedId={selectedId} setSelectedId={setSelectedId}
          goPrev={goPrev} goNext={goNext} goToGroup={goToGroup}
          expandedGroups={expandedGroups} toggleExpand={toggleExpand}
          handleSearchChange={handleSearchChange}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          disciplineFilter={disciplineFilter} setDisciplineFilter={setDisciplineFilter}
          dhFilter={dhFilter} setDhFilter={setDhFilter}
          nonStdRevFilter={nonStdRevFilter} setNonStdRevFilter={setNonStdRevFilter}
          filters={filters} loadingList={loadingList} listError={listError}
        />

        <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
          <PdfPanel
            itrId={detail?.id}
            hasPdf={detail?.has_pdf}
            hasSourcePdf={detail?.has_source_pdf}
            sourcePageRange={detail?.page_range}
            loading={loadingDetail}
          />
        </Box>

        <Box sx={{ width: TEXT_PANEL_WIDTH, flexShrink: 0, borderLeft: 1, borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TextPanel
            detail={detail} selectedPage={selectedPage} setSelectedPage={setSelectedPage}
            pageText={pageText} loadingDetail={loadingDetail} loadingText={loadingText}
            onSave={handleSaveDetail} savingDetail={savingDetail} saveError={saveError}
            catalogue={catalogue} onOpenResplit={openResplitModal}
          />
        </Box>
      </Box>

      <PageRangeModal
        open={resplitModal.open}
        step={resplitModal.step}
        pageStart={resplitModal.pageStart}
        pageEnd={resplitModal.pageEnd}
        loading={resplitModal.loading}
        previewData={resplitModal.previewData}
        previewBlobUrl={resplitModal.previewBlobUrl}
        draft={resplitModal.draft}
        setDraft={(updater) =>
          setResplitModal((prev) => ({
            ...prev,
            draft: typeof updater === 'function' ? updater(prev.draft) : updater,
          }))
        }
        error={resplitModal.error}
        saving={resplitModal.saving}
        currentDetail={detail}
        catalogue={catalogue}
        onClose={closeResplitModal}
        onPreview={handleResplitPreview}
        onBack={() => setResplitModal((prev) => ({ ...prev, step: 'input', error: '' }))}
        onConfirm={handleConfirmResplit}
      />

      <Snackbar
        open={importSnack.open}
        autoHideDuration={4000}
        onClose={() => setImportSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={importSnack.severity} onClose={() => setImportSnack((s) => ({ ...s, open: false }))}>
          {importSnack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReviewPage;
